import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Plus, Upload } from 'lucide-react';
import {
  type CatalogHardwareDetails,
  type CatalogHardwareListItem,
  type CreateCatalogHardwareRequest,
  type ListCatalogHardwareQuery,
} from '@kreart/shared';
import { Button } from '@/components/ui/button';
import { CatalogTable, type ColumnDef } from './CatalogTable';
import { XlsxDropzone } from './XlsxDropzone';
import { HardwareEditDrawer } from './HardwareEditDrawer';
import { PriceImpactDialog } from './PriceImpactDialog';
import { hardwareApi } from './api';
import { CatalogTabs } from '../CatalogTabs';

const PAGE_SIZE = 25;

export function HardwareAdminPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<CatalogHardwareDetails | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [impactTarget, setImpactTarget] = useState<{
    kind: string;
    id: string;
    label: string;
  } | null>(null);

  const query: ListCatalogHardwareQuery = {
    page,
    pageSize: PAGE_SIZE,
    search: search || undefined,
    includeDeleted: false,
  };

  const list = useQuery({
    queryKey: ['admin-hardware', query],
    queryFn: () => hardwareApi.list(query),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-hardware'], exact: false });

  const createMut = useMutation({
    mutationFn: (body: CreateCatalogHardwareRequest) => hardwareApi.create(body),
    onSuccess: () => invalidate(),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: CreateCatalogHardwareRequest }) =>
      hardwareApi.update(id, body),
    onSuccess: () => invalidate(),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => hardwareApi.remove(id),
    onSuccess: () => invalidate(),
  });

  const columns: ColumnDef<CatalogHardwareListItem>[] = [
    {
      key: 'name',
      label: t('common.name', { defaultValue: 'Name' }),
      render: (h) => <span className="font-medium">{h.name}</span>,
    },
    { key: 'internalCode', label: 'Cod Intern', render: (h) => h.internalCode ?? '—' },
    { key: 'sku', label: 'Cod Produs', render: (h) => h.sku ?? '—' },
    {
      key: 'manufacturer',
      label: t('common.manufacturer', { defaultValue: 'Manufacturer' }),
      render: (h) => h.manufacturer ?? '—',
    },
    {
      key: 'pricePerPiece',
      label: t('hardwareAdmin.pricePerPiece', { defaultValue: 'Price / piece' }),
      render: (h) => (h.pricePerPiece ? `${h.pricePerPiece} ${h.currency}` : '—'),
    },
    {
      key: 'timeCircularSec',
      label: t('hardwareAdmin.timeCircularSec', { defaultValue: 'Circular (s)' }),
      render: (h) => h.timeCircularSec,
    },
    {
      key: 'timeCncSec',
      label: t('hardwareAdmin.timeCncSec', { defaultValue: 'CNC (s)' }),
      render: (h) => h.timeCncSec,
    },
    {
      key: 'timeCantSec',
      label: t('hardwareAdmin.timeCantSec', { defaultValue: 'Cant (s)' }),
      render: (h) => h.timeCantSec,
    },
    {
      key: 'timeAssemblySec',
      label: t('hardwareAdmin.timeAssemblySec', { defaultValue: 'Assembly (s)' }),
      render: (h) => h.timeAssemblySec,
    },
    {
      key: 'active',
      label: t('common.active', { defaultValue: 'Active' }),
      render: (h) => (h.active ? '✓' : '—'),
    },
  ];

  const onAdd = () => {
    setEditing(null);
    setDrawerOpen(true);
  };
  const onEdit = async (row: CatalogHardwareListItem) => {
    const d = await hardwareApi.get(row.id);
    setEditing(d);
    setDrawerOpen(true);
  };
  const onDelete = async (row: CatalogHardwareListItem) => {
    if (!window.confirm(t('common.confirmDelete', { defaultValue: 'Delete?' }))) return;
    await deleteMut.mutateAsync(row.id);
  };
  const onSubmit = async (body: CreateCatalogHardwareRequest) => {
    if (editing) {
      const beforePrice = editing.pricePerPiece ?? null;
      const updated = await updateMut.mutateAsync({ id: editing.id, body });
      setDrawerOpen(false);
      const afterPrice = updated.pricePerPiece ?? null;
      if (beforePrice !== null && afterPrice !== null && beforePrice !== afterPrice) {
        setImpactTarget({
          kind: 'hardware',
          id: editing.id,
          label: t('priceImpact.hardwareLabel', {
            defaultValue: 'Hardware: {{name}} ({{before}} → {{after}})',
            name: updated.name,
            before: beforePrice,
            after: afterPrice,
          }),
        });
      }
    } else {
      await createMut.mutateAsync(body);
      setDrawerOpen(false);
    }
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-semibold">
          {t('hardwareAdmin.title', { defaultValue: 'Hardware (Feronerie)' })}
        </h1>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            type="button"
            variant="outline"
            onClick={() => setImportOpen(true)}
            data-testid="hardware-table-import"
          >
            <Upload className="mr-1.5 h-4 w-4" />
            {t('catalogAdmin.import', { defaultValue: 'Import' })}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => void hardwareApi.exportXlsx()}
            data-testid="hardware-table-export"
          >
            <Download className="mr-1.5 h-4 w-4" />
            {t('catalogAdmin.export', { defaultValue: 'Export' })}
          </Button>
          <Button type="button" onClick={onAdd} data-testid="hardware-table-add">
            <Plus className="mr-1.5 h-4 w-4" />
            {t('hardwareAdmin.create', { defaultValue: 'New hardware' })}
          </Button>
        </div>
      </div>
      <CatalogTabs />
      <CatalogTable<CatalogHardwareListItem>
        columns={columns}
        data={list.data?.items ?? []}
        total={list.data?.total ?? 0}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        searchValue={search}
        onSearchChange={(v) => {
          setPage(1);
          setSearch(v);
        }}
        onRowEdit={onEdit}
        onRowDelete={onDelete}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => list.refetch()}
        testId="hardware-table"
      />
      <HardwareEditDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        initial={editing}
        onSubmit={onSubmit}
      />
      <XlsxDropzone
        open={importOpen}
        onClose={() => setImportOpen(false)}
        entityLabel={t('hardwareAdmin.title', { defaultValue: 'Hardware' })}
        onPreview={async (f) => (await hardwareApi.importDryRun(f)).plan}
        onConfirm={async (f) => {
          await hardwareApi.importApply(f);
          await invalidate();
        }}
      />
      <PriceImpactDialog
        target={impactTarget}
        onClose={() => setImpactTarget(null)}
        fetchImpact={(id) => hardwareApi.getImpact(id)}
        recompute={(id) => hardwareApi.recomputeImpact(id)}
      />
    </div>
  );
}
